import { IAdvizerVideo } from "@/models/AdvizerVideo";
import { useAdvizer, useQuestions } from "@/store/selectors";
import {
  usePercentWatchedOfVideo,
  useSpecificVideoReaction,
  useUserAdvizersWatched,
  useUserVideosWatched,
} from "@/store/selectors/userProfileSelectors";
import { Avatar, Group, Space, Stack } from "@mantine/core";
import { useMemo } from "react";
import { UserProfileCard } from "./UserProfileCard";
import { Text } from "../ui/Text";
import { useStyles } from "@/styles/useStyles";
import { AdvizerTag } from "../AdvizerTag";
import { reactionTypeToColor, reactionTypeToIcon } from "@/configs/reactions";
import { UserProfileNoResults } from "./UserProfileNoResults";

export function UserProfileVideosWatched(props: { advizersOnly?: boolean }) {
  const { advizersOnly } = props;
  const userVideosWatched = useUserVideosWatched();
  const userAdvizersWatched = useUserAdvizersWatched();

  if (userVideosWatched.length === 0 && userAdvizersWatched.length === 0) {
    return (
      <UserProfileNoResults
        title={
          advizersOnly ? "No Advizers watched yet" : "No videos watched yet"
        }
        href="/explore"
        buttonText={"Explore Advizer videos"}
      />
    );
  }

  if (advizersOnly) {
    return (
      <Stack maw={700}>
        {userAdvizersWatched.map((advizer) => (
          <UserProfileAdvizerVideoCard
            key={advizer.id}
            advizerId={advizer.id}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack maw={700}>
      {userVideosWatched.map((video) => (
        <UserProfileAdvizerVideoCard key={video.id} video={video} />
      ))}
    </Stack>
  );
}

export function UserProfileAdvizerVideoCard(props: {
  video?: IAdvizerVideo;
  advizerId?: string;
}) {
  const { video, advizerId: advizerIdFromProps } = props;
  const advizerId = advizerIdFromProps || video?.advizerId;
  const { theme } = useStyles();
  const advizer = useAdvizer(advizerId || "");
  const questions = useQuestions();
  const percentWatchedOfVideo = usePercentWatchedOfVideo({
    vimeoId: video?.vimeoId || "",
  });
  const videoReaction = useSpecificVideoReaction({
    vimeoId: video?.vimeoId || "",
  });

  const hideQuestion = !video;

  const questionTitle = useMemo(() => {
    if (!video) return "";
    return questions.find((q) => q?.number === video?.questionNumber)?.title;
  }, [questions, video]);

  const badgeColor = useMemo(() => {
    if (!videoReaction) return "slate";
    return reactionTypeToColor[
      videoReaction.reaction as keyof typeof reactionTypeToColor
    ];
  }, [videoReaction]);

  const reactionIcon = useMemo(() => {
    if (!videoReaction) return undefined;
    const ReactionIcon =
      reactionTypeToIcon[
        videoReaction.reaction as keyof typeof reactionTypeToIcon
      ];
    const reactionColor =
      reactionTypeToColor[
        videoReaction.reaction as keyof typeof reactionTypeToColor
      ];
    if (!ReactionIcon) return undefined;
    const colorIndex = videoReaction.reaction === "negative" ? 6 : 7;
    return (
      <ReactionIcon size={14} color={theme.colors[reactionColor][colorIndex]} />
    );
  }, [videoReaction, theme.colors]);

  return (
    <UserProfileCard
      href={`/advizers/${advizerId}${hideQuestion ? "" : `?question=${video?.questionNumber}`}`}
      p={hideQuestion ? "lg" : undefined}
      badge={
        hideQuestion
          ? undefined
          : {
              color: badgeColor,
              label: `${Math.min(percentWatchedOfVideo, 100)}% watched`,
              leftSection: reactionIcon,
              variant: videoReaction ? "light" : "light",
            }
      }
    >
      <Group wrap="nowrap">
        <Avatar src={advizer?.profilePictureURL} size={hideQuestion ? "lg" : undefined} />
        <Stack gap={0} flex={1}>
          <Text flex={1} titleStyle={hideQuestion ? "h3" : "h5"} c="text.7" lh={1.2}>
            <span style={{ color: theme.colors.text[9] }}>
              {advizer?.firstName}
            </span>
            {`, ${advizer?.jobTitle} @ ${advizer?.company}`}
          </Text>
          {!hideQuestion ? (
            <Text titleStyle="h6" c="primary.7" fw={700}>
              Q{video?.questionNumber}: {questionTitle}
            </Text>
          ) : null}
        </Stack>
      </Group>
      {hideQuestion && advizerId ? (
        <>
          <Space h="md" />
          <AdvizerTags advizerId={advizerId} />
        </>
      ) : null}
    </UserProfileCard>
  );
}

function AdvizerTags(props: { advizerId: string }) {
  const { advizerId } = props;
  const advizer = useAdvizer(advizerId);
  const tags = advizer?.tags;
  return (
    <Group gap="xs">
      {tags?.map((tag) => <AdvizerTag key={tag.id} tag={tag} size="md" />)}
    </Group>
  );
}
